import { createStore, createActionsHook } from 'react-sweet-state';

/**
 * This state container is built for managing a user's "focus" state or when they're busy taking
 * an action while viewing an active page. Consumers of this state can set when the user is ready
 * or not ready for additional actions/content/notifications at a later time. Consumers that want to change
 * the user's focus state can simply import useUserFocusActions and call the setUserisFocused() function.
 *
 * Actions/content/notifications that we want to display to the user once they're free can be registered
 * to a map of callbacks via setUserReadyCallback(callback, type). When a user is ready, all registered
 * callbacks will be executed in a non-determined order.
 */
type UserFocusState = {
	isFocusing: boolean;
	userReadyCbMap: object;
	userFocusedCallback?: () => void;
};

const initialState: UserFocusState = {
	isFocusing: false,
	userReadyCbMap: {},
	userFocusedCallback: undefined,
};

export const setUserIsFocused =
	() =>
	({ setState, getState }) => {
		if (getState().userFocusedCallback) {
			getState().userFocusedCallback();
		}

		setState({
			isFocusing: true,
			userFocusedCallback: undefined,
		});
	};

export const setUserIsReady =
	() =>
	({ setState, getState }) => {
		setState({
			isFocusing: false,
		});

		Object.values(getState().userReadyCbMap).forEach((callback) => {
			if (typeof callback === 'function') {
				callback();
			}
		});

		setState({
			userReadyCbMap: {},
		});
	};

export const setUserReadyCallback =
	(callback, action) =>
	({ setState, getState }) => {
		// Check if the user is currently ready, if they are just execute
		if (!getState().isFocusing) {
			callback();
		} else {
			// Store a map of callback actions that the user has missed and will be informed about when they're ready

			// Keep a reference of any previous callbacks added
			const prev = getState().userReadyCbMap;

			setState({
				userReadyCbMap: {
					...prev,
					[action]: callback,
				},
			});
		}
	};

export const setUserFocusedCallback =
	(callback) =>
	({ setState, getState }) => {
		// if the user is already focused don't set a new callback
		if (!getState().isFocusing) {
			setState({
				userFocusedCallback: callback,
			});
		}
	};

const Store = createStore({
	initialState,
	actions: {
		setUserIsFocused,
		setUserIsReady,
		setUserReadyCallback,
		setUserFocusedCallback,
	},
	name: 'userFocusState',
});

export const useUserFocusActions = createActionsHook(Store);
